type ModalTarget = string | Element | null | undefined;

type ModalOptions = {
  backdrop?: boolean | 'static';
  keyboard?: boolean;
};

type ModalWindow = Window & {
  JidokaModal?: ModalService;
  jQuery?: (target: Element) => { triggerHandler: (eventName: string) => void };
  $?: (target: Element) => { triggerHandler: (eventName: string) => void };
};

export type ModalInstance = {
  show: () => void;
  hide: () => void;
  toggle: () => void;
  element: HTMLElement;
};

type ModalService = {
  show: (target: ModalTarget, options?: ModalOptions) => ModalInstance | null;
  hide: (target: ModalTarget) => ModalInstance | null;
  toggle: (target: ModalTarget, options?: ModalOptions) => ModalInstance | null;
  get: (target: ModalTarget) => ModalInstance | null;
  getOrCreate: (target: ModalTarget, options?: ModalOptions) => ModalInstance | null;
};

const instances = new WeakMap<HTMLElement, AppModal>();
let openCount = 0;
let activeModal: AppModal | null = null;
const modalStack: AppModal[] = [];
let previousBodyOverflow = '';
let previousBodyPaddingRight = '';

function getElement(target: ModalTarget): HTMLElement | null {
  if (!target) {
    return null;
  }

  if (typeof target === 'string') {
    return document.querySelector<HTMLElement>(target);
  }

  return target instanceof HTMLElement ? target : null;
}

function getFocusableElements(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(
    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
  )).filter((element) => !element.hasAttribute('disabled') && element.offsetParent !== null);
}

function getScrollbarWidth(): number {
  return window.innerWidth - document.documentElement.clientWidth;
}

function lockBodyScroll(): void {
  if (openCount > 0) {
    return;
  }

  previousBodyOverflow = document.body.style.overflow;
  previousBodyPaddingRight = document.body.style.paddingRight;
  const scrollbarWidth = getScrollbarWidth();

  document.body.classList.add('modal-open');
  document.body.style.overflow = 'hidden';

  if (scrollbarWidth > 0) {
    const currentPadding = parseFloat(window.getComputedStyle(document.body).paddingRight) || 0;
    document.body.style.paddingRight = `${currentPadding + scrollbarWidth}px`;
  }
}

function unlockBodyScroll(): void {
  if (openCount > 0) {
    return;
  }

  document.body.classList.remove('modal-open');
  document.body.style.overflow = previousBodyOverflow;
  document.body.style.paddingRight = previousBodyPaddingRight;
}

function emitModalEvent(element: HTMLElement, eventName: string): boolean {
  const event = new CustomEvent(eventName, { bubbles: true, cancelable: true });
  const allowed = element.dispatchEvent(event);
  const scopedWindow = window as ModalWindow;
  const jquery = scopedWindow.jQuery || scopedWindow.$;

  if (typeof jquery === 'function') {
    jquery(element).triggerHandler(eventName);
  }

  return allowed;
}

class AppModal implements ModalInstance {
  element: HTMLElement;

  private options: ModalOptions;

  private backdropElement: HTMLElement | null = null;

  private isOpen = false;

  private previouslyFocusedElement: HTMLElement | null = null;

  private previousElementZIndex = '';

  private readonly handleDocumentKeydown = (event: KeyboardEvent) => {
    if (!this.isOpen || activeModal !== this) {
      return;
    }

    if (event.key === 'Escape') {
      if (this.options.keyboard === false) {
        return;
      }

      event.preventDefault();
      this.hide();
      return;
    }

    if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  };

  private ignoreBackdropClick = false;

  private readonly handleDocumentMouseDown = (event: MouseEvent) => {
    if (!this.isOpen || activeModal !== this) {
      return;
    }

    const dialog = this.element.querySelector('.modal-dialog');
    this.ignoreBackdropClick = Boolean(dialog?.contains(event.target as Node));
  };

  private readonly handleBackdropClick = () => {
    if (this.ignoreBackdropClick) {
      return;
    }

    if (this.options.backdrop === 'static') {
      this.element.classList.add('modal-static');
      window.setTimeout(() => this.element.classList.remove('modal-static'), 150);
      return;
    }

    if (this.options.backdrop !== false) {
      this.hide();
    }
  };

  private readonly handleModalClick = (event: MouseEvent) => {
    if (event.target !== this.element) {
      return;
    }

    if (this.ignoreBackdropClick) {
      return;
    }

    this.handleBackdropClick();
  };

  constructor(element: HTMLElement, options: ModalOptions = {}) {
    this.element = element;
    this.options = this.readOptions(options);
  }

  show(): void {
    if (this.isOpen || !emitModalEvent(this.element, 'show.bs.modal')) {
      return;
    }

    this.previouslyFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.options = this.readOptions(this.options);
    const stackIndex = openCount;
    lockBodyScroll();
    openCount += 1;
    modalStack.push(this);
    activeModal = this;
    this.previousElementZIndex = this.element.style.zIndex;

    if (this.options.backdrop !== false) {
      this.createBackdrop(stackIndex);
    }

    this.element.style.zIndex = `${1050 + (stackIndex * 20)}`;
    this.element.style.display = 'block';
    this.element.removeAttribute('aria-hidden');
    this.element.setAttribute('aria-modal', 'true');
    this.element.setAttribute('role', this.element.getAttribute('role') || 'dialog');
    this.element.classList.add('show');
    this.element.addEventListener('click', this.handleModalClick);
    document.addEventListener('keydown', this.handleDocumentKeydown);
    document.addEventListener('mousedown', this.handleDocumentMouseDown, true);

    const focusTarget = getFocusableElements(this.element)[0] || this.element;
    focusTarget.focus({ preventScroll: true });

    this.isOpen = true;
    emitModalEvent(this.element, 'shown.bs.modal');
  }

  hide(): void {
    if (!this.isOpen || !emitModalEvent(this.element, 'hide.bs.modal')) {
      return;
    }

    this.isOpen = false;
    this.element.removeEventListener('click', this.handleModalClick);
    document.removeEventListener('keydown', this.handleDocumentKeydown);
    document.removeEventListener('mousedown', this.handleDocumentMouseDown, true);
    this.ignoreBackdropClick = false;
    this.element.classList.remove('show');
    this.element.style.display = 'none';
    this.element.style.zIndex = this.previousElementZIndex;
    this.element.setAttribute('aria-hidden', 'true');
    this.element.removeAttribute('aria-modal');
    this.removeBackdrop();

    openCount = Math.max(0, openCount - 1);
    const stackPosition = modalStack.indexOf(this);
    if (stackPosition >= 0) {
      modalStack.splice(stackPosition, 1);
    }
    if (activeModal === this) {
      activeModal = modalStack[modalStack.length - 1] ?? null;
    }
    unlockBodyScroll();

    if (this.previouslyFocusedElement && document.contains(this.previouslyFocusedElement)) {
      this.previouslyFocusedElement.focus({ preventScroll: true });
    }

    emitModalEvent(this.element, 'hidden.bs.modal');
  }

  toggle(): void {
    if (this.isOpen) {
      this.hide();
      return;
    }

    this.show();
  }

  updateOptions(options: ModalOptions = {}): void {
    this.options = this.readOptions(options);
  }

  private readOptions(options: ModalOptions): ModalOptions {
    const backdrop = this.element.dataset.modalBackdrop || this.element.dataset.backdrop;
    const keyboard = this.element.dataset.modalKeyboard || this.element.dataset.keyboard;

    return {
      backdrop: backdrop === 'static' ? 'static' : backdrop === 'false' ? false : options.backdrop ?? true,
      keyboard: keyboard === 'false' ? false : options.keyboard ?? true
    };
  }

  private createBackdrop(stackIndex: number): void {
    this.removeBackdrop();
    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop fade show';
    backdrop.style.zIndex = `${1040 + (stackIndex * 20)}`;
    backdrop.addEventListener('click', this.handleBackdropClick);
    document.body.appendChild(backdrop);
    this.backdropElement = backdrop;
  }

  private removeBackdrop(): void {
    if (!this.backdropElement) {
      return;
    }

    this.backdropElement.removeEventListener('click', this.handleBackdropClick);
    this.backdropElement.remove();
    this.backdropElement = null;
  }

  private trapFocus(event: KeyboardEvent): void {
    const focusable = getFocusableElements(this.element);
    if (!focusable.length) {
      event.preventDefault();
      this.element.focus({ preventScroll: true });
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

function getOrCreate(target: ModalTarget, options: ModalOptions = {}): ModalInstance | null {
  const element = getElement(target);
  if (!element) {
    return null;
  }

  let instance = instances.get(element);
  if (!instance) {
    instance = new AppModal(element, options);
    instances.set(element, instance);
  } else {
    instance.updateOptions(options);
  }

  return instance;
}

function initModalDataApi(): void {
  document.addEventListener('click', (event) => {
    const trigger = (event.target as Element | null)?.closest<HTMLElement>('[data-modal-target]');
    if (trigger) {
      const selector = trigger.dataset.modalTarget || trigger.getAttribute('href');
      if (selector && selector.startsWith('#')) {
        event.preventDefault();
        getOrCreate(selector)?.show();
        return;
      }
    }

    const dismiss = (event.target as Element | null)?.closest<HTMLElement>('[data-modal-dismiss]');
    if (dismiss) {
      event.preventDefault();
      const modal = dismiss.closest<HTMLElement>('.modal');
      getOrCreate(modal)?.hide();
    }
  });
}

export function initModalAdapter(): void {
  const scopedWindow = window as ModalWindow;

  scopedWindow.JidokaModal = {
    show(target, options) {
      const instance = getOrCreate(target, options);
      instance?.show();
      return instance;
    },
    hide(target) {
      const instance = getOrCreate(target);
      instance?.hide();
      return instance;
    },
    toggle(target, options) {
      const instance = getOrCreate(target, options);
      instance?.toggle();
      return instance;
    },
    get(target) {
      const element = getElement(target);
      return element ? instances.get(element) ?? null : null;
    },
    getOrCreate
  };

  initModalDataApi();
}
